import type {Nodes} from 'mdast'

import {isPositionedNode} from './positioned-node'

/** Utility type to get the child type of a parent node. */
export type ChildOf<Parent extends Nodes> = NonNullable<Parent['children']>[number]

/**
 * Insert `newChild` at `index` in `node`'s children, expanding the size of `node` to include the child if necessary.
 * Does not validate the index (ie, does not compare position to other children). Mutates `node`.
 * @param node The node to modify. Start position will be expanded to include `newChild`'s position if necessary.
 * @param index Index at which to insert the new child. Use negative indices to count from the end.
 * @param newChild The new child to modify. Since only delimiters can exist under every node, this must be a delimiter.
 */
export function insertChild<Node extends Nodes>(node: Node, index: number, newChild: ChildOf<Node>) {
  node.children ??= []
  node.children.splice(index, 0, newChild)

  // When updating positions, be sure to copy the source object so that two nodes don't share one object instance.
  // That could have surprising side effects if anything gets mutated later.
  if (isPositionedNode(node) && isPositionedNode(newChild))
    if (newChild.position.start.offset < node.position.start.offset)
      Object.assign(node.position.start, newChild.position.start)
    else if (newChild.position.end.offset > node.position.end.offset)
      Object.assign(node.position.end, newChild.position.end)
}

/** `insertChild` at the end of the parent. */
export function pushChild<Node extends Nodes>(node: Node, newChild: ChildOf<Node>) {
  insertChild(node, node.children?.length ?? 0, newChild)
}

/** `insertChild` at the beginning of the parent. */
export function unshiftChild<Node extends Nodes>(node: Node, newChild: ChildOf<Node>) {
  insertChild(node, 0, newChild)
}

/** Check if two nodes are immediately adjacent (touching). */
export function isAdjacent(a: Nodes, b: Nodes) {
  return isPositionedNode(a) && isPositionedNode(b) && a.position.end.offset === b.position.start.offset
}

/**
 * Remove the child at `index` from `node`, reducing the size of `node` if `shrink` is `true`. Mutates `node`.
 * @param node The node to modify.
 * @param index The index of the child to remove. Use negative indices to count from the end.
 * @param shrink If `true` and **only if** the child was touching the end of the node, the parent `node` position will
 * be updated to exclude the position of the removed child.
 * @returns The removed child, if one was found at that index.
 */
export function removeChild<Node extends Nodes>(node: Node, index: number, shrink = false): ChildOf<Node> | undefined {
  const child = node.children?.splice(index, 1)[0]

  if (shrink && child && isPositionedNode(node) && isPositionedNode(child))
    if (child.position.start.offset === node.position.start.offset)
      Object.assign(node.position.start, child.position.end)
    else if (child.position.end.offset === node.position.end.offset)
      Object.assign(node.position.end, child.position.start)

  return child
}

/** `removeChild` at the end of the parent. */
export function popChild<Node extends Nodes>(node: Node, shrink = false): ChildOf<Node> | undefined {
  return removeChild(node, -1, shrink)
}

/** `removeChild` at the beginning of the parent. */
export function shiftChild<Node extends Nodes>(node: Node, shrink = false): ChildOf<Node> | undefined {
  return removeChild(node, 0, shrink)
}

/**
 * Iterate over the children of a parent node in reverse order. Yields tuples of `[index, child]`. Iterating in
 * reverse can be useful as it allows you to remove children from the parent as you reach them without modifying
 * the indices of the children you haven't encountered yet.
 */
export function* reverseChildrenEntries<T extends Nodes>(parent: T): Generator<[index: number, child: ChildOf<T>]> {
  if (!parent.children) return

  for (let i = parent.children.length - 1; i >= 0; i--) {
    const child = parent.children[i]
    if (!child) continue
    yield [i, child]
  }
}
